import { useToast } from "@chakra-ui/react";
import { useCallback } from "react";

import { encodeBase64String } from "../../../utils/base64";
import {
  AddAiInterviewerVersionMutationVariables,
  AiInterviewerDocument,
  AiInterviewerQuery,
  AiInterviewerQueryVariables,
  AiInterviewerStatus,
  useAddAiInterviewerVersionMutation,
  useDuplicateAiInterviewerMutation,
  useUnarchiveAiInterviewerMutation,
  useUpdateAiInterviewerMutation,
} from "../../graphql";
import { AiInterviewerFormValues, MutationResult } from "./types";

interface UseAiInterviewerProps {
  aiInterviewerId: string;
}

interface UseAiInterviewerReturn {
  updateInterviewerName: MutationResult<(newName: string) => Promise<any>>;
  updateInterviewerStatus: MutationResult<
    (newStatus: AiInterviewerStatus) => Promise<any>
  >;
  updateInterviewerPosition: MutationResult<
    (positionId: string | undefined) => Promise<any>
  >;
  addInterviewerVersion: MutationResult<
    (
      data: AiInterviewerFormValues,
      appliedSuggestionIds?: string[],
      callback?: () => void
    ) => Promise<any>
  >;
  duplicateAiInterviewer: MutationResult<
    (refetchQueries?: string[]) => Promise<any>
  >;
  unarchiveAiInterviewer: MutationResult<
    (refetchQueries?: string[]) => Promise<any>
  >;
}

const useAiInterviewer = ({
  aiInterviewerId,
}: UseAiInterviewerProps): UseAiInterviewerReturn => {
  const toast = useToast();

  // Name
  // This is not used currently - editing name happens in version update
  // This can be reinstated for editable name in header pending product call
  const [updateNameMutation, { loading: loadingName, error: errorName }] =
    useUpdateAiInterviewerMutation({
      onError: (apolloError) => {
        toast({
          title: "Error updating AI Interviewer name",
          description:
            apolloError.message ||
            "An unexpected error occurred. Please try again.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      },
    });
  const updateInterviewerName = useCallback(
    (newName: string) => {
      return updateNameMutation({
        variables: { id: aiInterviewerId, name: newName },
      });
    },
    [updateNameMutation, aiInterviewerId]
  );

  // Status
  const [updateStatusMutation, { loading: loadingStatus, error: errorStatus }] =
    useUpdateAiInterviewerMutation({
      onError: (apolloError) => {
        toast({
          title: "Error updating AI Interviewer status",
          description:
            apolloError.message ||
            "An unexpected error occurred. Please try again.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      },
    });
  const updateInterviewerStatus = useCallback(
    (newStatus: AiInterviewerStatus) => {
      return updateStatusMutation({
        variables: { id: aiInterviewerId, status: newStatus },
      });
    },
    [updateStatusMutation, aiInterviewerId]
  );

  // Position (for ATSless orgs)
  const [
    updatePositionMutation,
    { loading: loadingPosition, error: errorPosition },
  ] = useUpdateAiInterviewerMutation({
    onError: (apolloError) => {
      toast({
        title: "Error updating position",
        description:
          apolloError.message ||
          "An unexpected error occurred. Please try again.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    },
  });
  const updateInterviewerPosition = useCallback(
    (positionId: string | undefined) => {
      return updatePositionMutation({
        variables: { id: aiInterviewerId, positionId: positionId ?? "" },
      });
    },
    [updatePositionMutation, aiInterviewerId]
  );

  // Main save changes - creates a new interviewer version
  const [
    addVersionMutation,
    { loading: loadingQuestions, error: errorQuestions },
  ] = useAddAiInterviewerVersionMutation({
    update: (cache, { data }) => {
      const savedAiInterviewer = data?.addAiInterviewerVersion?.aiInterviewer;
      if (!savedAiInterviewer) {
        return;
      }
      cache.writeQuery<AiInterviewerQuery, AiInterviewerQueryVariables>({
        query: AiInterviewerDocument,
        variables: { aiInterviewerId },
        data: {
          aiInterviewer: savedAiInterviewer,
        },
      });
    },
    onError: (apolloError) => {
      toast({
        title: "Error updating AI Interviewer",
        description:
          apolloError.message ||
          "An unexpected error occurred. Please try again.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    },
  });

  const addInterviewerVersion = useCallback(
    (
      data: AiInterviewerFormValues,
      appliedSuggestionIds?: string[],
      callback?: () => void
    ) => {
      const sanitizedQuestions = data.questions.map(
        ({ sortOrder, __typename, originalQuestionId, ...rest }) => ({
          ...rest,
        })
      );

      // Prepare email configs with base64 encoded body HTML
      const invitationWithEncodedHtml = {
        ...data.emailConfig.invitation,
        bodyHtml:
          encodeBase64String(data.emailConfig.invitation.bodyHtml) ?? "",
      };
      const reminderWithEncodedHtml = {
        ...data.emailConfig.reminder,
        bodyHtml: encodeBase64String(data.emailConfig.reminder.bodyHtml) ?? "",
      };
      const completionWithEncodedHtml = {
        ...data.emailConfig.completion,
        bodyHtml:
          encodeBase64String(data.emailConfig.completion.bodyHtml) ?? "",
      };
      const variables: AddAiInterviewerVersionMutationVariables = {
        aiInterviewerId,
        aiInterviewerDisplayName: data.displayName,
        questions: sanitizedQuestions,
        inviteExpirationDays: Number(data.inviteExpirationDays),
        reminderDays: Number(data.reminderDays),
        reminderEmailEnabled: data.reminderEmailEnabled,
        completionEmailEnabled: data.completionEmailEnabled,
        aiInterviewerName: data.name,
        positionInfo: data.positionInfo || null,
        introduction: data.introduction || null,
        closing: data.closing || null,
        jobDescription: data.jobDescription || null,
        verbatim: data.verbatim === "true",
        customQuestionWeightsEnabled: data.customQuestionWeightsEnabled,
        videoMode: data.videoSetting,
        appliedSuggestionIds:
          appliedSuggestionIds && appliedSuggestionIds.length > 0
            ? appliedSuggestionIds
            : undefined,
        invitationEmailConfig: JSON.stringify(invitationWithEncodedHtml),
        reminderEmailConfig: JSON.stringify(reminderWithEncodedHtml),
        completionEmailConfig: JSON.stringify(completionWithEncodedHtml),
        completionNotificationEmails: (
          data.completionNotificationEmails ?? []
        ).filter((e) => e.trim()),
        invitationSmsEnabled: data.invitationSmsEnabled,
        invitationSmsMessage: data.invitationSmsMessage,
      };

      return addVersionMutation({
        variables,
        onCompleted: (result) => {
          const savedVersion =
            result.addAiInterviewerVersion?.aiInterviewer?.currentVersion;
          if (
            data.reminderEmailEnabled &&
            savedVersion &&
            !savedVersion.reminderEmailEnabled
          ) {
            const days = Number(data.inviteExpirationDays);
            toast({
              title: "Reminder emails disabled",
              description: `Reminder emails were automatically disabled because the invite expiration window is ${days} ${
                days === 1 ? "day" : "days"
              }.`,
              status: "info",
              duration: 9000,
              isClosable: true,
            });
          }
          callback?.();
        },
      });
    },
    [addVersionMutation, aiInterviewerId]
  );

  // Duplicate
  const [
    duplicateMutation,
    { loading: loadingDuplicate, error: errorDuplicate },
  ] = useDuplicateAiInterviewerMutation({
    onError: (apolloError) => {
      toast({
        title: "Error duplicating AI Interviewer",
        description:
          apolloError.message ||
          "An unexpected error occurred. Please try again.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    },
  });
  const duplicateAiInterviewer = useCallback(
    (refetchQueries?: string[]) => {
      return duplicateMutation({
        variables: { aiInterviewerId },
        refetchQueries,
      });
    },
    [duplicateMutation, aiInterviewerId]
  );

  // Unarchive
  const [
    unarchiveMutation,
    { loading: loadingUnarchive, error: errorUnarchive },
  ] = useUnarchiveAiInterviewerMutation({
    onError: (apolloError) => {
      toast({
        title: "Error unarchiving AI Interviewer",
        description:
          apolloError.message ||
          "An unexpected error occurred. Please try again.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    },
  });
  const unarchiveAiInterviewer = useCallback(
    (refetchQueries?: string[]) => {
      return unarchiveMutation({
        variables: { id: aiInterviewerId },
        refetchQueries,
      });
    },
    [unarchiveMutation, aiInterviewerId]
  );

  return {
    updateInterviewerName: {
      mutation: updateInterviewerName,
      loading: loadingName,
      error: errorName,
    },
    updateInterviewerStatus: {
      mutation: updateInterviewerStatus,
      loading: loadingStatus,
      error: errorStatus,
    },
    updateInterviewerPosition: {
      mutation: updateInterviewerPosition,
      loading: loadingPosition,
      error: errorPosition,
    },
    addInterviewerVersion: {
      mutation: addInterviewerVersion,
      loading: loadingQuestions,
      error: errorQuestions,
    },
    duplicateAiInterviewer: {
      mutation: duplicateAiInterviewer,
      loading: loadingDuplicate,
      error: errorDuplicate,
    },
    unarchiveAiInterviewer: {
      mutation: unarchiveAiInterviewer,
      loading: loadingUnarchive,
      error: errorUnarchive,
    },
  };
};

export default useAiInterviewer;
